import { memo } from 'react';

import { ShareModalSectionLabel } from '~/components/ShareModal/components/ShareModalSectionLabel';
import { CreateShowcaseDesignBrandContextSection } from '~/components/ShowcaseModal/CreateShowcaseDesignBrandContextSection';
import { CreateShowcaseDesignButtonsHeadlinesRow } from '~/components/ShowcaseModal/CreateShowcaseDesignButtonsHeadlinesRow';
import { CreateShowcaseDesignCornerRoundingRow } from '~/components/ShowcaseModal/CreateShowcaseDesignCornerRoundingRow';
import { CreateShowcaseDesignCoverButton } from '~/components/ShowcaseModal/CreateShowcaseDesignCoverButton';
import { CreateShowcaseDesignCoverWidthRow } from '~/components/ShowcaseModal/CreateShowcaseDesignCoverWidthRow';
import { CreateShowcaseDesignFadeRow } from '~/components/ShowcaseModal/CreateShowcaseDesignFadeRow';
import { CreateShowcaseDesignHeaderBackgroundRow } from '~/components/ShowcaseModal/CreateShowcaseDesignHeaderBackgroundRow';
import { CreateShowcaseDesignHeadlineFontRow } from '~/components/ShowcaseModal/CreateShowcaseDesignHeadlineFontRow';
import { CreateShowcaseDesignLogoButton } from '~/components/ShowcaseModal/CreateShowcaseDesignLogoButton';
import { CreateShowcaseDesignPageBackgroundRow } from '~/components/ShowcaseModal/CreateShowcaseDesignPageBackgroundRow';
import { CreateShowcaseDesignPageThemeRow } from '~/components/ShowcaseModal/CreateShowcaseDesignPageThemeRow';
import { CreateShowcaseDesignPageWidthRow } from '~/components/ShowcaseModal/CreateShowcaseDesignPageWidthRow';
import { CreateShowcaseDesignPreview } from '~/components/ShowcaseModal/CreateShowcaseDesignPreview';
import {
  CreateShowcaseDesignProvider,
  type CreateShowcaseDesignProviderProps,
} from '~/components/ShowcaseModal/CreateShowcaseDesignProvider';

/** The injected create-mode draft data + commit sinks the modal supplies to the Design tab. */
export type CreateShowcaseDesignTabProps = CreateShowcaseDesignProviderProps;

/**
 * Design tab for the create-collection-link modal. Thin composition layer: it wraps the injected
 * draft data + commit sinks in {@link CreateShowcaseDesignProvider} and renders the
 * Brand context section, live preview, and the ordered Site design rows directly — each row reads
 * what it needs from `useCreateShowcaseDesign()` rather than threading props, so rows can be
 * reordered freely here.
 */
export const CreateShowcaseDesignTab = memo((props: CreateShowcaseDesignTabProps) => (
  <CreateShowcaseDesignProvider {...props}>
    <div className="flex flex-col gap-2" data-testid="CREATE_COLLECTION_LINK_DESIGN_TAB">
      <CreateShowcaseDesignBrandContextSection />
      <div className="mt-3">
        <CreateShowcaseDesignPreview />
      </div>
      <div className="mt-4">
        <div className="flex flex-col gap-2">
          <ShareModalSectionLabel title="Site design" />
          {/*
            Many controls below are preview-only: no backend `presentationSettings` field exists for
            them yet, so they drive the live preview only and are never persisted. TODO: persist when
            a presentationSettings field exists.
          */}
          <div className="flex flex-col gap-2">
            <div className="my-1 flex gap-2">
              <CreateShowcaseDesignLogoButton />
              <CreateShowcaseDesignCoverButton />
            </div>
            <CreateShowcaseDesignCoverWidthRow />
            <CreateShowcaseDesignFadeRow />
            <CreateShowcaseDesignHeadlineFontRow />
            <CreateShowcaseDesignPageWidthRow />
            <CreateShowcaseDesignCornerRoundingRow />
            <CreateShowcaseDesignPageThemeRow />
            <CreateShowcaseDesignButtonsHeadlinesRow />
            <CreateShowcaseDesignHeaderBackgroundRow />
            <CreateShowcaseDesignPageBackgroundRow />
          </div>
        </div>
      </div>
    </div>
  </CreateShowcaseDesignProvider>
));

CreateShowcaseDesignTab.displayName = 'CreateShowcaseDesignTab';
